import { reactive, readonly } from 'vue'
import type { FeatureFlag } from './feature-flags'
import featureService from './feature.service'
import cacheService from './feature-flags-cache.service'

interface FeatureFlagsState {
  flags: Record<FeatureFlag, boolean>
  initialized: boolean
}

const _eagerCache = cacheService.readCache()

const state = reactive<FeatureFlagsState>({
  flags: _eagerCache ?? ({} as Record<FeatureFlag, boolean>),
  initialized: _eagerCache !== null,
})

const setFlags = (flags: Record<FeatureFlag, boolean>) => {
  state.flags = flags
  state.initialized = true
}

const setFlag = (key: FeatureFlag, value: boolean) => {
  state.flags[key] = value
}

const isEnabled = (key: FeatureFlag): boolean => {
  return state.flags[key] ?? false
}

const isDisabled = (key: FeatureFlag): boolean => {
  return !isEnabled(key)
}

const reset = () => {
  state.flags = {} as Record<FeatureFlag, boolean>
  state.initialized = false
}

/**
 * Initializes feature flags during app bootstrap.
 * Uses Stale-While-Revalidate strategy:
 * - If cache exists: load from cache immediately, then revalidate in background
 * - If no cache: fetch from API (blocking)
 * Never throws - errors result in empty flags (fail-closed).
 *
 * @returns Promise that always resolves (never rejects)
 */
const initFeatures = async (): Promise<void> => {
  const cached = cacheService.readCache()

  if (cached !== null) {
    setFlags(cached)

    // Revalidate in background (non-blocking)
    featureService.fetchFeatureFlags().then((flags) => {
      if (Object.keys(flags).length > 0) {
        setFlags(flags)
        cacheService.writeCache(flags)
      }
    })
    return
  }

  const flags = await featureService.fetchFeatureFlags()
  setFlags(flags)

  if (Object.keys(flags).length > 0) {
    cacheService.writeCache(flags)
  }
}

/** @internal Test-only export for resetting state between tests */
export const _testReset = reset

export default {
  state: readonly(state),
  setFlags,
  setFlag,
  isEnabled,
  isDisabled,
  initFeatures,
}
